"use client";

import {useCallback, useEffect, useMemo, useState} from "react";
import {usePathname, useRouter, useSearchParams} from "next/navigation";
import {z} from "zod";
import {ScriptProposalReviewContext, ScriptProposalSideConversationContext} from "@/contexts/chat/ScriptProposalReviewContext";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import type {ScriptDirection} from "@/utils/scriptVersioning/paths";

const SCRIPT_PATH = /^\/(inbound|outbound)\/scripts?\/([a-f0-9-]+)(?:\/(?:latest|draft|\d+))?$/;
const uuid = z.string().uuid();

function scriptPathTarget(pathname: string | null): {direction: ScriptDirection; scriptId: string} | null {
   const match = SCRIPT_PATH.exec(pathname ?? "");
   if (!match || !uuid.safeParse(match[2]).success) return null;
   return {direction: match[1] === "inbound" ? "inbound" : "outbound", scriptId: match[2]};
}

/**
 * Reload the authenticated conversation, never reconstruct a proposal from today's script.
 * The provider is always mounted so ending a review (dropping its query) never remounts
 * the page or the dock beneath it, and the conversation stays beside the script it just
 * changed until the person leaves that script.
 */
export default function ScriptProposalPage({children}: {children: React.ReactNode}) {
   const search = useSearchParams();
   const pathname = usePathname();
   const router = useRouter();
   const {isDockAvailable, showConversationOnRight} = useGailDock();
   const [host, setHost] = useState<HTMLDivElement | null>(null);
   const [hasReview, setHasReview] = useState(false);
   const [reviewed, setReviewed] = useState<{scriptId: string; conversationId: string} | null>(null);
   const proposalId = search.get("proposalId");
   const conversationId = search.get("conversationId");
   const script = useMemo(() => scriptPathTarget(pathname), [pathname]);
   const target = useMemo(() => script && uuid.safeParse(proposalId).success && uuid.safeParse(conversationId).success
      ? {...script, proposalId: proposalId!, conversationId: conversationId!} : null, [script, proposalId, conversationId]);
   const finish = useCallback(() => {
      if (!target) return;
      setReviewed({scriptId: target.scriptId, conversationId: target.conversationId});
      showConversationOnRight(target.conversationId);
      router.replace(pathname, {scroll: false});
   }, [target, pathname, router, showConversationOnRight]);
   const reviewConversationId = target?.conversationId;
   useEffect(() => {
      if (isDockAvailable && reviewConversationId) showConversationOnRight(reviewConversationId);
   }, [isDockAvailable, reviewConversationId, showConversationOnRight]);
   const review = useMemo(() => target && {...target, host, setHost, hasReview, setHasReview, finish},
      [target, host, hasReview, finish]);
   const sideConversationId = target?.conversationId ??
      (reviewed && reviewed.scriptId === script?.scriptId ? reviewed.conversationId : null);
   return <ScriptProposalReviewContext.Provider value={review}>
      <ScriptProposalSideConversationContext.Provider value={sideConversationId}>{children}</ScriptProposalSideConversationContext.Provider>
   </ScriptProposalReviewContext.Provider>;
}
